import React, { useMemo } from 'react';
import { Server } from 'lucide-react';

const PlatformHealthComparison = ({ data }) => {
  const platformData = useMemo(() => {
    const platforms = {};
    
    data.forEach(item => {
      const platform = item.raw_data?.u_app_platform || 'Unknown';
      if (!platforms[platform]) {
        platforms[platform] = {
          healthy: 0,
          degraded: 0,
          unhealthy: 0,
          unknown: 0,
          total: 0
        };
      }
      
      platforms[platform].total++;
      
      if (item.health === 'HEALTHY') platforms[platform].healthy++;
      else if (item.health === 'DEGRADED') platforms[platform].degraded++;
      else if (item.health === 'UNHEALTHY') platforms[platform].unhealthy++;
      else platforms[platform].unknown++;
    });

    return Object.entries(platforms)
      .map(([name, stats]) => ({
        name,
        ...stats,
        healthPercentage: ((stats.healthy / stats.total) * 100).toFixed(1)
      }))
      .sort((a, b) => b.total - a.total);
  }, [data]);

  const maxTotal = Math.max(...platformData.map(p => p.total), 1);

  return (
    <div className="bg-dark-card rounded-lg border border-dark-border p-6">
      <div className="flex items-center gap-3 mb-6">
        <Server size={24} className="text-cyan-accent" />
        <h3 className="text-xl font-bold text-cyan-accent">Platform Health Comparison</h3>
      </div>

      <div className="space-y-4">
        {platformData.map((platform, idx) => (
          <div key={platform.name} className="bg-dark-border/20 rounded-lg p-4 border border-dark-border/60">
            {/* Platform Header */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="text-lg font-bold text-white">{platform.name}</div>
                <div className="text-sm text-gray-500">({platform.total} services)</div>
              </div>
              <div className="text-2xl font-bold text-cyan-accent">
                {platform.healthPercentage}%
              </div>
            </div>

            {/* Stacked Bar */}
            <div className="relative h-8 bg-dark-border/40 rounded-lg overflow-hidden border border-dark-border/60">
              <div className="absolute inset-0 flex">
                {/* Healthy */}
                {platform.healthy > 0 && (
                  <div
                    className="bg-lime-accent transition-all"
                    style={{
                      width: `${(platform.healthy / platform.total) * 100}%`,
                      boxShadow: 'inset 0 0 20px rgba(178, 255, 89, 0.3)'
                    }}
                    title={`Healthy: ${platform.healthy}`}
                  />
                )}
                {/* Degraded */}
                {platform.degraded > 0 && (
                  <div
                    className="bg-yellow-accent transition-all"
                    style={{
                      width: `${(platform.degraded / platform.total) * 100}%`,
                      boxShadow: 'inset 0 0 20px rgba(234, 179, 8, 0.3)'
                    }}
                    title={`Degraded: ${platform.degraded}`}
                  />
                )}
                {/* Unhealthy */}
                {platform.unhealthy > 0 && (
                  <div
                    className="bg-red-500 transition-all"
                    style={{
                      width: `${(platform.unhealthy / platform.total) * 100}%`,
                      boxShadow: 'inset 0 0 20px rgba(239, 68, 68, 0.3)'
                    }}
                    title={`Unhealthy: ${platform.unhealthy}`}
                  />
                )}
                {/* Unknown */}
                {platform.unknown > 0 && (
                  <div
                    className="bg-gray-600 transition-all"
                    style={{
                      width: `${(platform.unknown / platform.total) * 100}%`,
                      boxShadow: 'inset 0 0 20px rgba(107, 114, 128, 0.3)'
                    }}
                    title={`Unknown: ${platform.unknown}`}
                  />
                )}
              </div>
            </div>

            {/* Stats */}
            <div className="mt-3 flex items-center justify-between text-xs">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-lime-accent"></div>
                  <span className="text-gray-400">{platform.healthy} Healthy</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-yellow-accent"></div>
                  <span className="text-gray-400">{platform.degraded} Degraded</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-red-500"></div>
                  <span className="text-gray-400">{platform.unhealthy} Unhealthy</span>
                </div>
                {platform.unknown > 0 && (
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-gray-600"></div>
                    <span className="text-gray-400">{platform.unknown} Unknown</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Summary */}
      <div className="mt-6 pt-4 border-t border-dark-border/50 grid grid-cols-4 gap-4">
        <div className="text-center">
          <div className="text-2xl font-bold text-lime-accent">
            {platformData.reduce((sum, p) => sum + p.healthy, 0)}
          </div>
          <div className="text-xs text-gray-400 mt-1">Total Healthy</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-yellow-accent">
            {platformData.reduce((sum, p) => sum + p.degraded, 0)}
          </div>
          <div className="text-xs text-gray-400 mt-1">Total Degraded</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-red-500">
            {platformData.reduce((sum, p) => sum + p.unhealthy, 0)}
          </div>
          <div className="text-xs text-gray-400 mt-1">Total Unhealthy</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-cyan-accent">
            {platformData.length}
          </div>
          <div className="text-xs text-gray-400 mt-1">Platforms</div>
        </div>
      </div>
    </div>
  );
};

export default PlatformHealthComparison;
